import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Plus, Bookmark, Heart, Mail, QrCode } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { categories } from '../../data/constants';

export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const path = useLocation().pathname.replace(/\/+$/, '');
  // GET SMART QR on homepage + whole Smart QR workflow; Help Us Grow everywhere else
  const isHomepage = path === '' || path.startsWith('/smart-business-qr') || path.startsWith('/manage/') || path.startsWith('/b/');

  return (
    <header className="bg-white border-b border-slate-100 sticky top-0 z-50" data-testid="header">
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="JustOffs" className="h-10" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-3">
            <Link to="/post-ad" className="flex items-center gap-1.5 bg-orange-500 text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-orange-600 transition" data-testid="post-ad-btn">
              <Plus className="w-4 h-4" /> Post Free Ad
            </Link>
            <Link to="/bookmarks" className="flex items-center gap-1.5 text-slate-400 px-3 py-2 rounded-lg font-medium text-sm hover:text-slate-600 hover:bg-slate-50 transition" data-testid="saved-link">
              <Bookmark className="w-4 h-4" /> Saved
            </Link>
            {isHomepage ? (
              <Link to="/smart-business-qr" className="flex items-center gap-1.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2.5 rounded-full font-bold text-sm shadow-md shadow-orange-500/30 hover:from-orange-600 hover:to-orange-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 transition-all" data-testid="get-smart-qr-link">
                <QrCode className="w-4 h-4" /> GET SMART QR
              </Link>
            ) : (
              <Link to="/support" className="flex items-center gap-1.5 text-slate-400 px-3 py-2 rounded-lg font-medium text-sm hover:text-slate-600 hover:bg-slate-50 transition" data-testid="support-link">
                <Heart className="w-4 h-4" /> Help Us Grow
              </Link>
            )}
            <Link to="/contact" className="flex items-center gap-1.5 text-slate-400 px-3 py-2 rounded-lg font-medium text-sm hover:text-slate-600 hover:bg-slate-50 transition" data-testid="contact-link">
              <Mail className="w-4 h-4" /> Contact
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="text-slate-600 px-3 py-2 rounded-lg font-medium text-sm hover:bg-slate-50 transition" data-testid="admin-link">Admin</Link>
            )}
            {user?.role === 'admin' && (
              <button onClick={logout} className="text-slate-500 px-3 py-2 text-sm font-medium hover:text-slate-700 transition" data-testid="logout-btn">Logout</button>
            )}
          </nav>

          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 text-slate-600" data-testid="mobile-menu-btn">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {menuOpen && (
          <nav className="md:hidden pt-4 pb-2 border-t border-slate-100 mt-3 flex flex-col gap-1" data-testid="mobile-menu">
            <Link to="/post-ad" className="flex items-center gap-2 text-slate-700 py-2.5 px-3 rounded-lg hover:bg-slate-50" onClick={() => setMenuOpen(false)}><Plus className="w-4 h-4 text-orange-500" /> Post Free Ad</Link>
            <Link to="/bookmarks" className="flex items-center gap-2 text-slate-500 py-2.5 px-3 rounded-lg hover:bg-slate-50" onClick={() => setMenuOpen(false)}><Bookmark className="w-4 h-4" /> Saved Deals</Link>
            {isHomepage ? (
              <Link to="/smart-business-qr" className="flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-3 px-3 rounded-full shadow-md shadow-orange-500/30" onClick={() => setMenuOpen(false)} data-testid="mobile-get-smart-qr-link"><QrCode className="w-4 h-4" /> GET SMART QR</Link>
            ) : (
              <Link to="/support" className="flex items-center gap-2 text-slate-500 py-2.5 px-3 rounded-lg hover:bg-slate-50" onClick={() => setMenuOpen(false)}><Heart className="w-4 h-4" /> Help Us Grow</Link>
            )}
            <Link to="/contact" className="flex items-center gap-2 text-slate-500 py-2.5 px-3 rounded-lg hover:bg-slate-50" onClick={() => setMenuOpen(false)}><Mail className="w-4 h-4" /> Contact Us</Link>
            {user?.role === 'admin' && <Link to="/admin" className="text-slate-700 py-2.5 px-3 rounded-lg hover:bg-slate-50" onClick={() => setMenuOpen(false)}>Admin Panel</Link>}
            {user?.role === 'admin' && (
              <button onClick={() => { logout(); setMenuOpen(false); }} className="text-left text-slate-500 py-2.5 px-3">Logout</button>
            )}
          </nav>
        )}
      </div>

      {/* Categories Strip */}
      <div className="border-t border-slate-100 bg-slate-50/50">
        <div className="max-w-6xl mx-auto px-2 sm:px-4 py-2">
          <div className="flex justify-start sm:justify-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map(cat => (
              <Link key={cat.id} to={`/deals?category=${cat.id}`}
                className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:shadow-sm transition flex-shrink-0" data-testid={`category-${cat.id}`}>
                <div className={`w-5 h-5 ${cat.color} rounded flex items-center justify-center`}>
                  <cat.icon className="w-3 h-3 text-white" />
                </div>
                <span className="text-xs font-medium text-slate-600 hidden sm:inline">{cat.name}</span>
                <span className="text-xs font-medium text-slate-600 sm:hidden">{cat.short}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};
